import {useEffect, useMemo} from 'react';

import * as analytics from 'analytics/Analytics';
import {ActionOrigin, STUDIES_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {getMaxRowsConfig} from 'notebook/structured-tables/util/checkTableCapacity';
import {ExperimentalInputs} from 'procedures/planning/ProcedurePlanTypes';
import {ProcedureRunDataEntryMode} from 'studies/StudyDAO';
import {LargestTableTypeSizes} from 'studies/typed/shared/ExperimentalInputs';
import {assertNever} from 'util/Types';

import {Condition} from './useExperimentDesignState';

// Keep in-sync with benchling/procedures/domain/procedure_run_handler.py
export const MAX_METHOD_EXECUTION_INSTANCES = 500;
export const MAX_PLAN_CONDITION_COUNT = 100;

export interface MethodExecutionLimitState {
  /** Stores whether user has surpassed limit for the number of method execution instances */
  isOverLimit: boolean;
  /** The total number of condition-replicates that can be added to each method */
  replicateLimit: number;
  /**
   * The number of remaining condition-replicates that can be added to each method.
   * This is calculated by subtracting the current number of condition-replicates from the replicate limit.
   */
  remainingReplicates: number;
  /** Stores the number of condition-replicates the user tried to use */
  replicateCount: number;
  /** Stores the number of conditions the user tried to use */
  conditionCount: number;
}

export function useMethodExecutionLimitState(
  experimentalInputs: ExperimentalInputs | null,
  conditions: Array<Condition> | null,
  largestTableTypeSizes: LargestTableTypeSizes | null,
  dataEntryMode: ProcedureRunDataEntryMode
): MethodExecutionLimitState {
  // The total number of replicates that can be added, before the method execution instance limit is reached.
  const methodExecutionReplicateLimit = useMemo(
    () =>
      experimentalInputs != null
        ? Math.floor(MAX_METHOD_EXECUTION_INSTANCES / Object.keys(experimentalInputs).length)
        : MAX_METHOD_EXECUTION_INSTANCES,
    [experimentalInputs]
  );

  // The total number of replicates that can be added, before the table row limit is reached.
  const tableReplicateLimit = useMemo(() => {
    const tableSizeLimits = (largestTableTypeSizes ?? []).map(({size, tableKind}) => {
      const tableLimit = getMaxRowsConfig(true, tableKind);
      return {tableKind, size, replicateLimit: Math.floor(tableLimit / size)};
    });
    return Math.min(...tableSizeLimits.map((limit) => limit.replicateLimit));
  }, [largestTableTypeSizes]);

  // Calculate the total number of condition-replicates by summing the replicates across all conditions.
  const replicateCount = useMemo(
    () => (conditions ?? []).map((condition) => condition.numReplicates).reduce((acc, curr) => acc + curr, 0),
    [conditions]
  );

  const methodExecutionLimitState = useMemo(() => {
    const replicateLimit = getReplicateLimit(
      tableReplicateLimit,
      methodExecutionReplicateLimit,
      dataEntryMode
    );
    const isOverLimit = replicateCount > replicateLimit;
    const remainingReplicates = Math.max(replicateLimit - replicateCount, 0);
    const conditionCount = conditions?.length ?? 1; // There is always at least one condition
    return {isOverLimit, remainingReplicates, replicateLimit, replicateCount, conditionCount};
  }, [replicateCount, tableReplicateLimit, methodExecutionReplicateLimit, dataEntryMode, conditions?.length]);

  useEffect(() => {
    if (methodExecutionLimitState.isOverLimit) {
      analytics.trackEvent(STUDIES_ANALYTICS_ACTIONS.WARNING_SHOWN_INVALID_CONDITIONS, {
        actionOrigin: ActionOrigin.STUDY_CREATION_MODAL,
        actionInstanceId: analytics.makeAnalyticsEventId(),
        warningType: 'exceeded_limits',
        limitExceeded: methodExecutionLimitState.replicateLimit,
        numItems: methodExecutionLimitState.replicateCount,
      });
    }
  }, [
    methodExecutionLimitState.isOverLimit,
    methodExecutionLimitState.replicateCount,
    methodExecutionLimitState.replicateLimit,
  ]);

  return methodExecutionLimitState;
}

function getReplicateLimit(
  tableReplicateLimit: number,
  methodExecutionReplicateLimit: number,
  dataEntryMode: ProcedureRunDataEntryMode
): number {
  switch (dataEntryMode) {
    case ProcedureRunDataEntryMode.INDIVIDUAL_CONDITION_REPLICATES: {
      return methodExecutionReplicateLimit;
    }
    case ProcedureRunDataEntryMode.ALL_CONDITION_REPLICATES_TOGETHER: {
      return Math.min(methodExecutionReplicateLimit, tableReplicateLimit);
    }
    default:
      assertNever(dataEntryMode);
      // This should never happen, but TypeScript requires a return value.
      return 0;
  }
}

export function isAtPlannedConditionsLimit(methodExecutionLimitState: MethodExecutionLimitState): boolean {
  return methodExecutionLimitState.conditionCount >= MAX_PLAN_CONDITION_COUNT;
}
